import { FormField } from '@/shared/ui/FormField';
import { AddressSuggestField } from './AddressSuggestField';
import { CityAutocompleteField } from './CityAutocompleteField';
import { WEEK_DAYS, type ScheduleValue } from './profileForm';
import type { ProfileFormApi } from './ProfileInputs';

const COORDINATE_LIMIT = { lat: 90, long: 180 };

const clampDigits = (part: string, max: number) => (Number(part) > max ? String(max) : part);

/** Legacy `getInputTimeValue`: keeps digits only and formats them as `HH:MM`. */
const formatTimeInput = (raw: string): string => {
  const digits = raw.replace(/\D/g, '').slice(0, 4);
  const hours = clampDigits(digits.slice(0, 2), 23);
  return digits.length > 2 ? `${hours}:${clampDigits(digits.slice(2), 59)}` : hours;
};

/** Legacy `processCoordinateInput`, but longitude may go up to 180: only valid numbers (or an empty value). */
const isCoordinate = (value: string, limit: number) =>
  value === '' || (!Number.isNaN(Number(value)) && Math.abs(Number(value)) <= limit);

const WeekSchedule = ({ form }: { form: ProfileFormApi }) => {
  const update = (name: string, change: Partial<ScheduleValue>) =>
    form.patch({
      schedule: form.values.schedule.map((day) => (day.name === name ? { ...day, ...change } : day)),
    });

  return (
    <FormField label="График работы" errors={form.errors.schedule_adv}>
      {form.values.schedule.map((day) => (
        <div key={day.name} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
          <label style={{ width: 50, fontWeight: 'normal', margin: 0 }}>
            <input
              type="checkbox"
              checked={day.isActive}
              onChange={(e) => update(day.name, { isActive: e.target.checked })}
            />{' '}
            {WEEK_DAYS.find((d) => d.name === day.name)?.label}
          </label>
          <input
            className="form-control input-sm"
            style={{ width: 80 }}
            placeholder="09:00"
            value={day.from}
            disabled={!day.isActive}
            onChange={(e) => update(day.name, { from: formatTimeInput(e.target.value) })}
          />
          —
          <input
            className="form-control input-sm"
            style={{ width: 80 }}
            placeholder="18:00"
            value={day.to}
            disabled={!day.isActive}
            onChange={(e) => update(day.name, { to: formatTimeInput(e.target.value) })}
          />
        </div>
      ))}
    </FormField>
  );
};

/** Service location: city + address suggest (legacy order), then coordinates and schedule. */
export const ServiceLocationFields = ({ form }: { form: ProfileFormApi }) => {
  const { lat, long } = form.values;

  const coordinateField = (field: 'lat' | 'long', label: string) => (
    <FormField label={label} errors={form.errors[field]}>
      <input
        className="form-control input-sm"
        style={{ maxWidth: 200 }}
        value={form.values[field]}
        onChange={(e) =>
          isCoordinate(e.target.value, COORDINATE_LIMIT[field]) && form.patch({ [field]: e.target.value }, field)
        }
      />
    </FormField>
  );

  return (
    <>
      <CityAutocompleteField form={form} />
      <AddressSuggestField form={form} />
      {coordinateField('lat', 'Широта')}
      {coordinateField('long', 'Долгота')}
      {lat && long && (
        <p>
          <a
            href={`https://yandex.ru/maps/?pt=${long},${lat}&z=16&l=map`}
            target="_blank"
            rel="noopener noreferrer"
          >
            Показать на карте
          </a>
        </p>
      )}
      <WeekSchedule form={form} />
    </>
  );
};
